const MS_PER_DAY = 24 * 60 * 60 * 1000;

const toUtcDateValue = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
};

const getInclusiveDayCount = (startDateValue, endDateValue) => {
  if (startDateValue == null || endDateValue == null) return 0;
  if (endDateValue < startDateValue) return 0;
  return Math.floor((endDateValue - startDateValue) / MS_PER_DAY) + 1;
};

const toLocalDateFromUtcValue = (value) => {
  if (value == null) return null;
  const date = new Date(value);
  return new Date(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate()
  );
};

const getCustomizedBasisPeriods = (company) => {
  if (
    !company?.isCustomizedBasisPeriod ||
    !Array.isArray(company?.basisPeriods) ||
    company.basisPeriods.length !== 2
  ) {
    return null;
  }

  const periods = [...company.basisPeriods].sort(
    (left, right) =>
      Number(left?.yearOfAssessment) - Number(right?.yearOfAssessment)
  );
  const [preceding, current] = periods;
  const precedingStart = toUtcDateValue(preceding?.financialYearStart);
  const precedingEnd = toUtcDateValue(preceding?.financialYearEnd);
  const currentStart = toUtcDateValue(current?.financialYearStart);
  const currentEnd = toUtcDateValue(current?.financialYearEnd);

  if (
    [precedingStart, precedingEnd, currentStart, currentEnd].some(
      (value) => value == null
    ) ||
    precedingEnd < precedingStart ||
    currentStart !== precedingEnd + MS_PER_DAY ||
    currentEnd < currentStart
  ) {
    return null;
  }

  return {
    preceding,
    current,
    precedingStart,
    precedingEnd,
    currentStart,
    currentEnd,
  };
};

export const roundApportionmentBasis = (value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return 0;
  return Number(numericValue.toFixed(4));
};

export const getPrecedingApportionmentBasis = (context) => {
  if (!context?.totalDays) return 0;

  if (context.precedingBasis != null) {
    return roundApportionmentBasis(context.precedingBasis);
  }

  return roundApportionmentBasis(context.precedingDays / context.totalDays);
};

export const hasCrossYASplitAmounts = (row) =>
  Object.prototype.hasOwnProperty.call(row || {}, "preceding_amount") ||
  Object.prototype.hasOwnProperty.call(row || {}, "current_amount");

const getNumericAmount = (value) => parseFloat(value) || 0;

export const clampCrossYAOffset = (value) => {
  const numericValue = parseInt(value, 10);
  if (Number.isNaN(numericValue)) return 0;
  return Math.max(-1, Math.min(1, numericValue));
};

export const getCrossYASplitTotal = (row) =>
  getNumericAmount(row?.preceding_amount) +
  getNumericAmount(row?.current_amount);

export const getCrossYATargetAmount = (row) => {
  const amount = getNumericAmount(row?.amount);
  if (amount !== 0 || !hasCrossYASplitAmounts(row)) return amount;
  return getCrossYASplitTotal(row);
};

export const reconcileCrossYAAmounts = (row, targetAmount = null) => {
  if (!row || !hasCrossYASplitAmounts(row)) return row;

  const amount =
    targetAmount == null
      ? getCrossYATargetAmount(row)
      : getNumericAmount(targetAmount);
  const precedingAmount = getNumericAmount(row.preceding_amount);
  const currentAmount = getNumericAmount(row.current_amount);
  const splitTotal = precedingAmount + currentAmount;
  const offset = amount - splitTotal;

  if (offset === 0) {
    return {
      ...row,
      amount,
      crossYAOffset: getNumericAmount(row.crossYAOffset),
      crossYAOffsetAppliedTo: row.crossYAOffsetAppliedTo || "",
      crossYAMismatchAmount: 0,
    };
  }

  return {
    ...row,
    amount,
    current_amount: currentAmount + offset,
    preceding_amount: precedingAmount,
    crossYAOffset: offset,
    crossYAOffsetAppliedTo: "current_amount",
    crossYAMismatchAmount: 0,
  };
};

export const applyCrossYAOffset = (row, offset) => {
  if (!row || !hasCrossYASplitAmounts(row)) return row;

  const nextOffset = clampCrossYAOffset(offset);
  const previousOffset = getNumericAmount(row.crossYAOffset);
  const precedingAmount = getNumericAmount(row.preceding_amount);
  const currentAmount =
    getNumericAmount(row.current_amount) - previousOffset + nextOffset;

  return {
    ...row,
    amount: precedingAmount + currentAmount,
    current_amount: currentAmount,
    preceding_amount: precedingAmount,
    crossYAOffset: nextOffset,
    crossYAOffsetAppliedTo: nextOffset === 0 ? "" : "current_amount",
    crossYAMismatchAmount: 0,
  };
};

export const getCrossYAApportionmentContext = (company, isCrossYA) => {
  if (!isCrossYA) return null;

  const basisStart = toUtcDateValue(company?.financialYearStart);
  const basisEnd = toUtcDateValue(company?.financialYearEnd);
  const yearOfAssessment = Number(company?.yearOfAssessment);

  if (
    basisStart == null ||
    basisEnd == null ||
    basisEnd < basisStart ||
    Number.isNaN(yearOfAssessment)
  ) {
    return null;
  }

  const customizedPeriods = getCustomizedBasisPeriods(company);
  if (customizedPeriods) {
    const precedingDays = getInclusiveDayCount(
      customizedPeriods.precedingStart,
      customizedPeriods.precedingEnd
    );
    const currentDays = getInclusiveDayCount(
      customizedPeriods.currentStart,
      customizedPeriods.currentEnd
    );
    const totalDays = precedingDays + currentDays;

    return {
      currentBasis: roundApportionmentBasis(currentDays / totalDays),
      currentDays,
      precedingBasis: roundApportionmentBasis(precedingDays / totalDays),
      precedingDays,
      totalDays,
    };
  }

  // The current YA is the latest 12-month period ending on the company's FYE.
  // For example, an FYE of 30 June 2026 has a current-YA basis period starting
  // on 1 July 2025. The preceding YA is the earlier remainder of the long period.
  const basisEndDate = new Date(basisEnd);
  const currentYABasisStart = Date.UTC(
    basisEndDate.getUTCFullYear(),
    basisEndDate.getUTCMonth() - 11,
    1
  );
  const precedingYAEnd = Math.min(basisEnd, currentYABasisStart - MS_PER_DAY);
  const totalDays = getInclusiveDayCount(basisStart, basisEnd);
  const precedingDays = getInclusiveDayCount(basisStart, precedingYAEnd);
  const currentDays = totalDays - precedingDays;

  if (totalDays <= 0 || precedingDays <= 0) return null;

  const precedingBasis = roundApportionmentBasis(precedingDays / totalDays);
  const currentBasis = roundApportionmentBasis(currentDays / totalDays);

  return {
    currentBasis,
    currentDays,
    precedingBasis,
    precedingDays,
    totalDays,
  };
};

export const getCrossYABasisPeriods = (company, isCrossYA) => {
  const context = getCrossYAApportionmentContext(company, isCrossYA);
  if (!context) return null;

  const customizedPeriods = getCustomizedBasisPeriods(company);
  if (customizedPeriods) {
    return {
      context,
      preceding: {
        basis: context.precedingBasis,
        days: context.precedingDays,
        financialYearStart: customizedPeriods.preceding.financialYearStart,
        financialYearEnd: customizedPeriods.preceding.financialYearEnd,
        yearOfAssessment: Number(
          customizedPeriods.preceding.yearOfAssessment
        ),
      },
      current: {
        basis: context.currentBasis,
        days: context.currentDays,
        financialYearStart: customizedPeriods.current.financialYearStart,
        financialYearEnd: customizedPeriods.current.financialYearEnd,
        yearOfAssessment: Number(customizedPeriods.current.yearOfAssessment),
      },
    };
  }

  const basisEnd = toUtcDateValue(company?.financialYearEnd);
  const yearOfAssessment = Number(company?.yearOfAssessment);
  const basisEndDate = new Date(basisEnd);
  const currentYABasisStart = Date.UTC(
    basisEndDate.getUTCFullYear(),
    basisEndDate.getUTCMonth() - 11,
    1
  );
  const precedingYAEnd = Math.min(basisEnd, currentYABasisStart - MS_PER_DAY);

  return {
    context,
    preceding: {
      basis: context.precedingBasis,
      days: context.precedingDays,
      financialYearStart: company?.financialYearStart,
      financialYearEnd: toLocalDateFromUtcValue(precedingYAEnd),
      yearOfAssessment: yearOfAssessment - 1,
    },
    current: {
      basis: context.currentBasis,
      days: context.currentDays,
      financialYearStart: toLocalDateFromUtcValue(currentYABasisStart),
      financialYearEnd: company?.financialYearEnd,
      yearOfAssessment,
    },
  };
};

export const getCrossYABasisPeriod = (company, isCrossYA, yaKey) => {
  const periods = getCrossYABasisPeriods(company, isCrossYA);
  if (!periods) return null;

  return yaKey === "preceding" || yaKey === "preceding_amount"
    ? periods.preceding
    : periods.current;
};

const apportionCrossYARow = (row, apportionmentContext) => {
  const hasAmount = Object.prototype.hasOwnProperty.call(row || {}, "amount");

  if (!apportionmentContext || !row || !hasAmount) {
    return row;
  }

  const originalAmount = getCrossYATargetAmount(row);

  if (hasCrossYASplitAmounts(row)) {
    return reconcileCrossYAAmounts(row, originalAmount);
  }

  const precedingAmount = Math.round(
    originalAmount * getPrecedingApportionmentBasis(apportionmentContext)
  );

  return reconcileCrossYAAmounts({
    ...row,
    amount: originalAmount,
    current_amount: originalAmount - precedingAmount,
    preceding_amount: precedingAmount,
    crossYAOffset: 0,
    crossYAOffsetAppliedTo: "",
    crossYAMismatchAmount: 0,
  });
};

export const apportionCrossYARows = (rows, apportionmentContext) =>
  Array.isArray(rows)
    ? rows.map((row) => apportionCrossYARow(row, apportionmentContext))
    : [];
